import { useState } from "react";
import AuthContext from "../../context/auth";
import { UserType } from "../../hooks/api/Login/interfaces/userInfo.response";

type UserInfoResponse = {
  id: number;
  name: string;
  type: UserType;
  email: string;
};

interface ProviderProps {
  children: any;
}

const TOKEN_KEY = "@token";
const USER_ROLE_KEY = "@userRole";
const USER_DATA_KEY = "@userData";

const readStoredUser = (): UserInfoResponse | null => {
  const raw = localStorage.getItem(USER_DATA_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as UserInfoResponse;
    if (!parsed?.id) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const readUserFromToken = (): UserInfoResponse | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as {
      id?: number;
      name?: string;
      email?: string;
      roles?: string[];
    };

    if (!payload?.id) {
      return null;
    }

    const storedRole = localStorage.getItem(USER_ROLE_KEY) as UserType | null;
    const roleFromToken = payload.roles?.includes("DOCTOR") ? "Doctor" : "Patient";

    return {
      id: payload.id,
      name: payload.name || "",
      email: payload.email || "",
      type: storedRole || roleFromToken,
    };
  } catch {
    return null;
  }
};

const persistUser = (user: UserInfoResponse) => {
  localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  localStorage.setItem(USER_ROLE_KEY, user.type);
};

export const AuthProvider: React.FC<ProviderProps> = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const [isSigned, setIsSigned] = useState<boolean>(
    !!localStorage.getItem(TOKEN_KEY) && !!localStorage.getItem(USER_ROLE_KEY)
  );

  const signIn = (token: string, user: UserInfoResponse) => {
    localStorage.setItem(TOKEN_KEY, token);
    persistUser(user);
    setIsSigned(true);
  };

  const signInWithToken = async (
    token: string,
    user: { id: number; name: string; email: string; roles: string[] },
    type: 'patient' | 'doctor'
  ) => {
    const userType: UserType = type === 'doctor' ? 'Doctor' : 'Patient';
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem('@integrationToken', token);
    persistUser({
      id: user.id,
      name: user.name,
      email: user.email,
      type: userType,
    });
    setIsSigned(true);
  };

  const signOut = () => {
    localStorage.clear();
    setIsSigned(false);
  };

  const getToken = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token;
  };

  const getUserData = (): UserInfoResponse => {
    const storedUser = readStoredUser();
    if (storedUser) {
      return storedUser;
    }

    const tokenUser = readUserFromToken();
    if (tokenUser) {
      persistUser(tokenUser);
      return tokenUser;
    }

    const userRole = localStorage.getItem(USER_ROLE_KEY) as UserType | null;
    return {
      id: 0,
      name: '',
      email: '',
      type: userRole || 'Patient',
    };
  };

  const updateUserData = (user: Partial<UserInfoResponse>) => {
    const currentUser = getUserData();
    persistUser({
      ...currentUser,
      ...user,
      type: user.type || currentUser.type,
    });
  };

  const contextValues = {
    isSigned,
    signIn,
    signInWithToken,
    updateUserData,
    signOut,
    getToken,
    getUserData,
  };

  return (
    <AuthContext.Provider value={contextValues}>
      {children}
    </AuthContext.Provider>
  );
};
